import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity';

export class AuthError extends Error {
  constructor(friendly, cause) {
    super(friendly);
    this.name = 'AuthError';
    this.friendly = friendly;
    this.cause = cause;
  }
}

function poolFor(config) {
  return new CognitoUserPool({
    UserPoolId: config.userPoolId,
    ClientId: config.userPoolClientId,
  });
}

/**
 * SRP: the password proves knowledge without ever being sent.
 * Cognito is what rejects a wrong password — not this code.
 */
export function signIn(config, username, password) {
  const user = new CognitoUser({ Username: username, Pool: poolFor(config) });
  const details = new AuthenticationDetails({ Username: username, Password: password });

  return new Promise((resolve, reject) => {
    user.authenticateUser(details, {
      onSuccess: async (session) => {
        try {
          resolve(await exchange(config, session.getIdToken().getJwtToken()));
        } catch (err) {
          reject(new AuthError('Signed in, but could not reach S3. Try again.', err));
        }
      },
      onFailure: (err) => {
        // preventUserExistenceErrors makes Cognito return the same code for a
        // bad username and a bad password, so this message must not tell them
        // apart either.
        const friendly =
          err?.code === 'NotAuthorizedException'
            ? 'Wrong username or password.'
            : err?.code === 'PasswordResetRequiredException'
              ? 'This account needs its password reset from the AWS CLI.'
              : 'Could not sign in. Check your connection and try again.';
        reject(new AuthError(friendly, err));
      },
      newPasswordRequired: () => {
        reject(
          new AuthError(
            'This account still has a temporary password. Set a permanent one with the AWS CLI.',
          ),
        );
      },
    });
  });
}

async function exchange(config, idToken) {
  const login = `cognito-idp.${config.region}.amazonaws.com/${config.userPoolId}`;
  const credentials = fromCognitoIdentityPool({
    client: new CognitoIdentityClient({ region: config.region }),
    identityPoolId: config.identityPoolId,
    logins: { [login]: idToken },
  });

  // Resolve once up front so a misconfigured pool fails at sign-in rather than
  // halfway through a 40-minute upload. The provider caches the result.
  const resolved = await credentials();
  return { idToken, identityId: resolved.identityId, credentials };
}

export function signOut(config, username) {
  if (!username) return;
  new CognitoUser({ Username: username, Pool: poolFor(config) }).signOut();
}
